import React, { useState, useEffect, useRef, useCallback } from "react";
import { PatientIDContext } from "../../Contexts";
import "../../../dist/assets/stylesheets/css/patients.css";
import ReviewChartsContainer from "./charts/ReviewChartsContainer";
import AnnotateContactsBar from "./AnnotateContactsBar";
import TrialDropDown from "./TrialDropDown";
import TrialLiveLayout from "./live/TrialLiveLayout";
import TrialVideoPlayback from "./TrialVideoPlayback";
import { fetchReviewPlayback } from "../../util/trialLiveLayoutApi";
import { useParams, useHistory, Link } from "react-router-dom";
import PreviewChartsContainer from "./charts/PreviewChartContainer";
import { Icon } from "@iconify/react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPatient } from "../../store/patient-slice";
import PatientInfoContainer from "../patients/patientDashboard/PatientInfoContainer";
import { getPatients } from "../../util/patientsApi";
import { updateCurrentTime } from "../../store/video-slice";
import { readVideoDurationSec, mergeVideoDurationSec } from "../../util/videoDuration";
import { getTrialInfo, updateTrialTagFields } from "../../util/trialApi";
import { exportTrialRaw } from "../../util/exportTrialRaw";
import { downloadTrialPoseAndDeviceCsv } from "../../util/trialPoseDeviceCsv";
import { saveContactAnnotations } from "../../util/metricsAPI";
import {
  toggleAnnotationAtPlayhead,
  createContactAnnotation,
  upsertContactAnnotation,
  removeContactAnnotation,
} from "../../util/contactAnnotations";
import { metricSideFromName } from "../../util/floorContacts";
import MetricDropdown from "./metricDropdowns/MetricDropdown";
import logo from "../logo.PNG";
import ConfirmModal from "../modals/ConfirmModal";
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import GaitReportModalContent from "./GaitReportModalContent";
import TrialTagsBar from "./TrialTagsBar";
import EditTrialTagsModal from "./EditTrialTagsModal";
import {
  buildTrialTagPayload,
  defaultTagValues,
  parseTrialTagsFromApi,
  SESSION_SCOPE,
  TRIAL_SCOPE,
} from "../../util/trialTagFields";
import {
  currentAccountKey,
  loadTagMemory,
  rememberTagValues,
} from "../../util/trialTagMemory";
import { getAllLegacyTrialsByUser } from "../../util/reportAPI";
import { formatRecordingDateTime } from "../../util/dateFormat";
import {
  LEGACY_ROOT_PATH,
  editableLegacyTags,
  hasBadTriangulation,
  legacyDashboardPath,
  legacyReviewPath,
  restoreProtectedLegacyTags,
} from "../../util/legacyAnalytics";
import LegacyTriangulationBadge from "../legacy/LegacyTriangulationBadge";


const DeleteButton = styled(Button)({
  boxShadow: 'none',
  textTransform: 'none',
  color: 'white',
  borderColor: '#DD6164',
  backgroundColor: '#DD6164',
  '&:hover': {
    backgroundColor: 'rgba(211, 97, 100, 0.7)',
  },
  '& svg': {
    marginRight: '5px',
    fontSize: '1.2rem',
  }
});

const PrintButton = styled(Button)({
  boxShadow: 'none',
  textTransform: 'none',
  color: 'white',
  borderColor: 'black',
  backgroundColor: 'black',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
  },
  '& svg': {
    marginRight: '5px',
    fontSize: '1.2rem',
  }
});

const ModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '60%',
  height: '60%',
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
};

function ReviewPatient(props) {
  // Legacy lab imports: listed from /a/legacy/..., fixed 60 Hz clock, no floor
  // detection, legacy EvoWalk channels, and import tags kept out of the editor.
  const { legacy = false } = props;
  const history = useHistory();
  const origin = history.location.state;
  const { patientID } = useParams();
  const dispatch = useDispatch();
  const [patientInfo, setPatientInfo] = useState(null);
  const [trialInfo, setTrialInfo] = useState(null);
  const params = useParams();
  const patient_id = Object.values(params)[0];
  const trial_id = Object.values(params)[1];
  const [trial, setTrial] = useState(trial_id);
  // The playhead lives in the store rather than component state: keeping it here
  // re-rendered the whole review page on every frame of playback.
  const [duration, setDuration] = useState(0);
  const [playerMode, setPlayerMode] = useState("loading");
  const [liveLayout, setLiveLayout] = useState(null);
  const [videoSrc, setVideoSrc] = useState(null);
  const [liveError, setLiveError] = useState(false);
  const [videoLayoutMode, setVideoLayoutMode] = useState("compact");
  const [metaDataLoaded, setMetaDataLoaded] = useState(false);
  const [confirmModal, setShowConfirmModal] = useState({
    show: false,
    acceptHandler: null,
  });
  const [openGenerateModal, setOpenGenerateModal] = useState(false);
  const [editTagsOpen, setEditTagsOpen] = useState(false);
  const [sessionTags, setSessionTags] = useState(() => defaultTagValues(SESSION_SCOPE));
  const [trialTags, setTrialTags] = useState(() => defaultTagValues(TRIAL_SCOPE));
  const [tagMemory, setTagMemory] = useState(() => loadTagMemory(currentAccountKey()));
  const [savingTags, setSavingTags] = useState(false);
  const [annotateMode, setAnnotateMode] = useState(false);
  const [annotateType, setAnnotateType] = useState('heel_contact');
  const [annotateSide, setAnnotateSide] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [savedAnnotations, setSavedAnnotations] = useState([]);
  const annotationsTouchedRef = useRef(false);
  const [annotationSaving, setAnnotationSaving] = useState(false);
  const [annotationStatus, setAnnotationStatus] = useState(null);
  const [csvError, setCsvError] = useState(null);
  const location = useLocation();
  const state = location.state || {};
  const backUrl = state.backUrl ?? state.url ?? `/patients/patient_dashboard/${patientID}`;
  const backName = state.backName ?? state.name ?? 'Dashboard';
  const dashboardUrl = legacy
    ? legacyDashboardPath(patientID)
    : `/patients/patient_dashboard/${patientID}`;
  const cameFromRecordings = !legacy && (
    backName === 'Recordings' || (typeof backUrl === 'string' && backUrl.includes('recordings'))
  );
  const breadcrumbFontSize = '1.25rem';

  const handleOpenGenerateModal = () => setOpenGenerateModal(true);
  const handleCloseGenerateModal = () => setOpenGenerateModal(false);
  const handleExportRaw = () => {
    exportTrialRaw(trial).catch((err) => {
      console.error('Failed to export raw trial data:', err);
    });
  };
  const handleDownloadCsv = useCallback(async () => {
    setCsvError(null);
    try {
      await downloadTrialPoseAndDeviceCsv(trial);
    } catch (err) {
      console.error('Failed to download trial CSV:', err);
      setCsvError(err && err.message ? err.message : 'Download failed');
    }
  }, [trial]);

  const confirm = () => {
    setShowConfirmModal({
      show: true,
      acceptHandler: deleteTrialHandler,
      message:
        "Are you sure you want to delete this trial? This action cannot be undone.",
    });
  };
  const deleteTrialHandler = () => {

  }
  useEffect(() => {
    annotationsTouchedRef.current = false;
    setAnnotateMode(false);
    setAnnotateSide(null);
    setAnnotations([]);
    setSavedAnnotations([]);
    setAnnotationStatus(null);
    setCsvError(null);
  }, [trial]);

  const applySavedAnnotations = useCallback((savedTrial, marks) => {
    if (String(savedTrial) !== String(trial)) return;
    if (annotationsTouchedRef.current) return;
    const next = Array.isArray(marks) ? marks : [];
    setAnnotations(next);
    setSavedAnnotations(next);
  }, [trial]);

  const placeAnnotation = useCallback((time, metricName) => {
    annotationsTouchedRef.current = true;
    const side = metricSideFromName(metricName);
    setAnnotations((prev) => upsertContactAnnotation(
      prev,
      createContactAnnotation(side, annotateType, time)
    ));
    setAnnotationStatus(null);
  }, [annotateType]);

  const saveAnnotations = useCallback(async () => {
    setAnnotationSaving(true);
    setAnnotationStatus(null);
    try {
      await saveContactAnnotations(trial, annotations);
      setSavedAnnotations(annotations);
      setAnnotationStatus('saved');
    } catch (err) {
      console.error('Failed to save contact annotations:', err);
      setAnnotationStatus('failed');
    } finally {
      setAnnotationSaving(false);
    }
  }, [trial, annotations]);

  const handleTrialChange = (id) => {
    // navigate('asdf')
    history.replace({
      pathname: legacy
        ? legacyReviewPath(patient_id, id)
        : "/patients/review_patient/" + patient_id + "/" + id,
      state: origin,
    });
    setTrial(id);
  };
  const videoRef = useRef();
  const placeAnnotationAtPlayhead = useCallback((side) => {
    if (side !== 'left' && side !== 'right') return;
    setAnnotateSide(side);
    const time = videoRef.current ? videoRef.current.currentTime : null;
    if (typeof time !== 'number' || !Number.isFinite(time)) return;
    annotationsTouchedRef.current = true;
    setAnnotations((prev) => toggleAnnotationAtPlayhead(prev, side, annotateType, time));
    setAnnotationStatus(null);
  }, [annotateType]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const lastSyncedTimeRef = useRef(null);
  const syncTimeFromVideo = useCallback(
    (time) => {
      if (typeof time !== 'number' || Number.isNaN(time)) return;
      if (lastSyncedTimeRef.current === time) return;
      lastSyncedTimeRef.current = time;
      dispatch(updateCurrentTime(time));
    },
    [dispatch]
  );

  const handleSeek = useCallback(
    (time) => {
      if (Number.isNaN(time)) return;
      if (videoRef.current) {
        videoRef.current.currentTime = time;
      }
      lastSyncedTimeRef.current = time;
      dispatch(updateCurrentTime(time));
    },
    [dispatch]
  );

  useEffect(() => {
    getPatients(patientID).then((res) => {
      setPatientInfo(res[0]);
    });
  }, [patientID]);

  useEffect(() => {
    getTrialInfo(trial).then((res) => {
      const info = res?.trial ?? null;
      setTrialInfo(info);
      const parsed = parseTrialTagsFromApi(info);
      if (legacy) parsed.trialTags.tags = editableLegacyTags(info?.tags);
      setSessionTags(parsed.sessionTags);
      setTrialTags(parsed.trialTags);
      // Seed the account vocabulary from tags already on this trial so Edit Tags
      // dropdowns offer previously used values even before a local save.
      const account = currentAccountKey();
      setTagMemory((prev) => rememberTagValues(account, prev, {
        ...parsed.sessionTags,
        ...parsed.trialTags,
      }));
    });
  }, [trial, legacy]);

  const handleTogglePlay = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      const played = el.play();
      if (played && typeof played.catch === "function") {
        played.catch((err) => {
          if (!err || err.name !== "AbortError") {
            console.error("Video play failed:", err);
          }
        });
      }
    } else {
      el.pause();
    }
  }, []);

  useEffect(() => {
    if (playerMode !== "combined" || !videoSrc || !metaDataLoaded) return undefined;
    let rafId = null;
    const tick = () => {
      const el = videoRef.current;
      if (el && !Number.isNaN(el.currentTime)) {
        syncTimeFromVideo(el.currentTime);
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [playerMode, videoSrc, metaDataLoaded, syncTimeFromVideo]);

  const handleVideoError = useCallback((e) => {
    console.error('Video loading error:', e);
    // Charts use API duration_sec when the video element has no usable duration.
    // Never leave metaDataLoaded false on error — that unmounts ReviewChartsContainer
    // and makes every angle/sensor series look "missing".
    setMetaDataLoaded(true);
    setDuration((prev) => {
      if (typeof prev === 'number' && prev > 0) return prev;
      return readVideoDurationSec(videoRef.current) ?? prev;
    });
  }, []);

  useEffect(() => {
    if (!trial) return undefined;
    let cancelled = false;
    setPlayerMode("loading");
    setLiveLayout(null);
    setVideoSrc(null);
    setLiveError(false);
    setMetaDataLoaded(false);
    setDuration(0);
    lastSyncedTimeRef.current = null;
    dispatch(updateCurrentTime(0));

    fetchReviewPlayback(trial)
      .then((playback) => {
        if (cancelled) return;
        if (playback.mode === "live") {
          setLiveLayout(playback.layout);
          setPlayerMode("live");
          const seconds = playback.layout.meta.frameCount / playback.layout.meta.fps;
          setDuration((prev) => (prev > seconds ? prev : seconds));
          return;
        }
        setVideoSrc(playback.videoSrc);
        setPlayerMode("combined");
        setMetaDataLoaded(false);
        setDuration(0);
      })
      .catch((err) => {
        console.error("Error in fetching review playback:", err);
        if (cancelled) return;
        setLiveError(true);
        setPlayerMode("unavailable");
        setMetaDataLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [trial, dispatch]);

  useEffect(() => {
    if (!liveLayout || metaDataLoaded) return undefined;
    const timer = window.setTimeout(() => {
      setMetaDataLoaded(true);
    }, 4000);
    return () => window.clearTimeout(timer);
  }, [liveLayout, metaDataLoaded]);

  // If the video element never fires loadedmetadata (codec / network stall),
  // still mount charts after a short wait so analytics is not blank forever.
  useEffect(() => {
    if (!videoSrc || metaDataLoaded) return undefined;
    const timer = window.setTimeout(() => {
      setMetaDataLoaded(true);
    }, 4000);
    return () => window.clearTimeout(timer);
  }, [videoSrc, metaDataLoaded]);


  // useEffect(() => {
  //   if (trial) {
  //     const fetchData = async () => {
  //       // const video = await fetch(window.SERVER_URL + "/a/video?trial=" + trial);
  //       const videoOption = (
  //         <video
  //           ref={videoRef}
  //           className={"view"}
  //           key={window.SERVER_URL + "/a/video?trial=" + trial}
  //           controls
  //           onLoadedMetadata={handleLoadedMetaData}
  //         >
  //           <source
  //             src={window.SERVER_URL + "/a/video?trial=" + trial}
  //             type="video/mp4"
  //           />
  //         </video>
  //       );
  //       console.log("setvideo");
  //       setVideos(videoOption);
  //     };
  //     fetchData();
  //   }
  // }, [trial]);

  // Fires for loadedmetadata, durationchange, progress and canplay: a streamed
  // video reports a provisional duration that grows while it buffers, so keep
  // the longest reading instead of the first one.
  const handleLoadedMetaData = useCallback((event) => {
    const next = readVideoDurationSec(event?.target ?? videoRef.current);
    setDuration((prev) => mergeVideoDurationSec(prev, next));
    setMetaDataLoaded(true);
  }, []);

  // The original camera files can run past the analysed window; stretching the
  // series over the file length would pull the curves off the contact markers,
  // which sit at their true frame times.
  const chartDuration =
    playerMode === "live" && liveLayout
      ? liveLayout.meta.frameCount / liveLayout.meta.fps
      : duration;

  const handleSaveTags = useCallback(async ({ sessionTags: nextSession, trialTags: nextTrial }) => {
    if (!trialInfo) return;
    setSavingTags(true);
    const payload = buildTrialTagPayload({
      sessionTags: nextSession,
      trialTags: nextTrial,
      subject: patientInfo?.patient_name || trialInfo.patient_name || trialInfo.sub,
      trialKey: trialInfo.trial,
      sessionKey: trialInfo.session,
    });
    if (legacy) payload.tags = restoreProtectedLegacyTags(payload.tags, trialInfo.tags);
    try {
      await updateTrialTagFields(trialInfo.id, payload);
      setSessionTags(nextSession);
      setTrialTags(nextTrial);
      setTrialInfo((prev) => (prev ? { ...prev, ...payload } : prev));
      const account = currentAccountKey();
      setTagMemory((prev) => rememberTagValues(account, prev, {
        ...nextSession,
        ...nextTrial,
      }));
      setEditTagsOpen(false);
    } catch (err) {
      console.error('Failed to save trial tags:', err);
    } finally {
      setSavingTags(false);
    }
  }, [trialInfo, patientInfo, legacy]);

  return (
    <PatientIDContext.Provider value={patientID}>
      <div className={`parent-container review-page review-page--video-${videoLayoutMode}`}>
        <Modal
          open={openGenerateModal}
          onClose={handleCloseGenerateModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={ModalStyle}>
            <GaitReportModalContent
              trial={trial}
              patientInfo={patientInfo}
              trialInfo={trialInfo}
              durationSec={duration}
            />
          </Box>
        </Modal>
        <EditTrialTagsModal
          open={editTagsOpen}
          onClose={() => setEditTagsOpen(false)}
          sessionTags={sessionTags}
          trialTags={trialTags}
          onSave={handleSaveTags}
          suggestions={tagMemory}
          saving={savingTags}
        />
        {confirmModal.show && (
          <ConfirmModal
            message={confirmModal.message}
            rejectHandler={() =>
              setShowConfirmModal({ ...confirmModal, show: false })
            }
            acceptHandler={confirmModal.acceptHandler}
          />
        )}

        <img className="printable logo" src={logo}></img>
        <Box width="100%" mt={2} mb={2}>
          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                flexWrap="wrap"
                sx={{ fontSize: breadcrumbFontSize, color: 'rgba(0,0,0,0.87)' }}
                className="trial-breadcrumb"
              >
                {legacy ? (
                  <>
                    <Link
                      to={LEGACY_ROOT_PATH}
                      className="trial-breadcrumb-link"
                      style={{ color: 'rgba(0,0,0,0.87)', textDecoration: 'none', fontWeight: 500 }}
                    >
                      Legacy analytics
                    </Link>
                    <span style={{ color: '#9ca3af' }}>{'>'}</span>
                    <Link
                      to={dashboardUrl}
                      className="trial-breadcrumb-link"
                      style={{ color: 'rgba(0,0,0,0.87)', textDecoration: 'none', fontWeight: 500 }}
                    >
                      Dashboard
                    </Link>
                    <span style={{ color: '#9ca3af' }}>{'>'}</span>
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      Trial
                    </Box>
                    <TrialDropDown
                      handleChange={handleTrialChange}
                      selectedOption={trial}
                      fontSize={breadcrumbFontSize}
                      className="trial-dropdown-breadcrumb"
                      loadTrials={getAllLegacyTrialsByUser}
                    />
                  </>
                ) : cameFromRecordings ? (
                  <>
                    <Link
                      to="/recordings"
                      className="trial-breadcrumb-link"
                      style={{ color: 'rgba(0,0,0,0.87)', textDecoration: 'none', fontWeight: 500 }}
                    >
                      Recordings
                    </Link>
                    <span style={{ color: '#9ca3af' }}>{'>'}</span>
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      Trial
                    </Box>
                    <TrialDropDown
                      handleChange={handleTrialChange}
                      selectedOption={trial}
                      fontSize={breadcrumbFontSize}
                      className="trial-dropdown-breadcrumb"
                    />
                  </>
                ) : (
                  <>
                    <Link
                      to="/patients"
                      className="trial-breadcrumb-link"
                      style={{ color: 'rgba(0,0,0,0.87)', textDecoration: 'none', fontWeight: 500 }}
                    >
                      Patients
                    </Link>
                    <span style={{ color: '#9ca3af' }}>{'>'}</span>
                    <Link
                      to={dashboardUrl}
                      className="trial-breadcrumb-link"
                      style={{ color: 'rgba(0,0,0,0.87)', textDecoration: 'none', fontWeight: 500 }}
                    >
                      Dashboard
                    </Link>
                    <span style={{ color: '#9ca3af' }}>{'>'}</span>
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      Trial
                    </Box>
                    <TrialDropDown
                      handleChange={handleTrialChange}
                      selectedOption={trial}
                      fontSize={breadcrumbFontSize}
                      className="trial-dropdown-breadcrumb"
                    />
                  </>
                )}
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
              {!legacy && (
                <DeleteButton onClick={confirm}>
                  <Icon icon="ant-design:delete-outlined" />
                  Delete Trial
                </DeleteButton>
              )}
              <PrintButton onClick={() => window.print()}>
                <Icon icon="material-symbols:print" />
                Print Page
              </PrintButton>
              <PrintButton onClick={handleOpenGenerateModal}>
                <Icon icon="carbon:document-pdf" />
                Generate Report
              </PrintButton>
              {!legacy && (
                <PrintButton onClick={handleExportRaw}>
                  <Icon icon="material-symbols:download" />
                  Export raw data
                </PrintButton>
              )}
              {!legacy && (
                <PrintButton onClick={handleDownloadCsv}>
                  <Icon icon="carbon:download" />
                  Download CSV file
                </PrintButton>
              )}
              </Box>
            </Box>
            {!legacy && csvError ? (
              <span role="alert">{csvError}</span>
            ) : null}
            {patientInfo && trialInfo && (
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Box display="flex" flexWrap="wrap" gap={2} sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>
                  <span><strong>Patient:</strong> {patientInfo.patient_name}</span>
                  <span><strong>Date:</strong> {trialInfo.name}</span>
                  {legacy && trialInfo.date ? (
                    <span>
                      <strong>Recorded:</strong>{' '}
                      {formatRecordingDateTime(trialInfo.date, trialInfo.recorded_timezone)}
                    </span>
                  ) : null}
                  {legacy && trialInfo.session ? (
                    <span><strong>Session:</strong> {trialInfo.session}</span>
                  ) : null}
                  {legacy && hasBadTriangulation(trialInfo) ? <LegacyTriangulationBadge /> : null}
                </Box>
                <TrialTagsBar
                  sessionTags={sessionTags}
                  trialTags={trialTags}
                  onEditTags={() => setEditTagsOpen(true)}
                />
              </Box>
            )}
          </Box>
        </Box>
        <Box
          display="flex"
          mb={2}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: '#fff',
            background: '#fff',
          }}
        >
          <Box display="flex" justifyContent="center" alignItems="stretch" borderRadius={3} flexBasis="100%" sx={{ backgroundColor: '#fff' }}>
            {playerMode === "combined" ? (
              <TrialVideoPlayback
                videoSrc={videoSrc}
                videoRef={videoRef}
                duration={duration}
                onLoadedMetadata={handleLoadedMetaData}
                onError={handleVideoError}
                onLayoutModeChange={setVideoLayoutMode}
                onTimeSync={syncTimeFromVideo}
                onSeek={handleSeek}
                onTogglePlay={handleTogglePlay}
              />
            ) : (
              <TrialLiveLayout
                layout={playerMode === "live" ? liveLayout : null}
                error={liveError}
                videoRef={videoRef}
                duration={duration}
                onLoadedMetadata={handleLoadedMetaData}
                onError={handleVideoError}
                onTimeSync={syncTimeFromVideo}
                onSeek={handleSeek}
                onLayoutModeChange={setVideoLayoutMode}
              />
            )}
          </Box>
        </Box>
        <AnnotateContactsBar
          active={annotateMode}
          onToggle={() => setAnnotateMode((on) => !on)}
          typeKey={annotateType}
          side={annotateSide}
          onTypeChange={setAnnotateType}
          annotations={annotations}
          savedAnnotations={savedAnnotations}
          saving={annotationSaving}
          status={annotationStatus}
          onSave={saveAnnotations}
          onPlaceAtPlayhead={placeAnnotationAtPlayhead}
        />
        <div>
          <MetricDropdown trial={trial} legacy={legacy}></MetricDropdown>
        </div>
        {metaDataLoaded && (
          <ReviewChartsContainer
            videoRef={videoRef}
            videoDuration={chartDuration}
            videoLayoutMode={videoLayoutMode}
            trial={trial}
            legacy={legacy}
            annotateMode={annotateMode}
            annotateType={annotateType}
            annotations={annotations}
            onPlaceAnnotation={placeAnnotation}
            onSavedAnnotations={applySavedAnnotations}
            onRemoveAnnotation={(id) => {
              annotationsTouchedRef.current = true;
              setAnnotationStatus(null);
              setAnnotations((prev) => removeContactAnnotation(prev, id));
            }}
          />
        )}
      </div>
    </PatientIDContext.Provider>
  );
}

export default ReviewPatient;
